import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminMenu from './adminMenu';
import '../css/Blog.css';

const BlogUpdateForm = ({ setIsAuthenticated }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [post, setPost] = useState({
        title: '',
        slug: '',
        content: '',
        author: '',
        createdAt: '',
        updatedAt: '',
        visible: true,
        imageUrl: ''
    });

    const [allPosts, setAllPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Fetch current post (for editing)
    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }

        async function fetchPost() {
            try {
                const response = await fetch(`/api/blog/${id}`);
                if (!response.ok) throw new Error('Failed to fetch post');
                const data = await response.json();

                data.createdAt = data.createdAt ? new Date(data.createdAt).toISOString().substring(0, 10) : '';
                data.updatedAt = data.updatedAt ? new Date(data.updatedAt).toISOString().substring(0, 10) : '';

                setPost(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        fetchPost();
    }, [id, isEditMode]);

    // Fetch all posts to list below the form
    useEffect(() => {
        async function fetchAllPosts() {
            try {
                const response = await fetch('/api/blog');
                if (!response.ok) throw new Error('Failed to fetch posts');
                const data = await response.json();
                setAllPosts(data);
            } catch (err) {
                console.error(err);
            }
        }

        fetchAllPosts();
    }, []);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setPost(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');

        const now = new Date().toISOString();

        const postToSave = {
            ...post,
            updatedAt: now,
            createdAt: isEditMode ? post.createdAt : now
        };

        const url = isEditMode ? `/api/blog/${id}` : `/api/blog`;
        const method = isEditMode ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(postToSave)
            });

            if (!response.ok) {
                const errorMsg = await response.text();
                throw new Error(errorMsg || 'Failed to save post');
            }

            navigate('/BlogPage');
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <p>Loading...</p>;

    if (error) return <p style={{ color: 'red' }}>{error}</p>;
    BlogUpdateForm.modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean']
        ]
    };

    BlogUpdateForm.formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link', 'image'
    ];

    return (
        <div className="admin-container">
            <AdminMenu setIsAuthenticated={setIsAuthenticated} />
            <div className="update-blog" style={{ maxWidth: 600, margin: '2rem auto', color: '#fff' }}>
            <form onSubmit={handleSubmit} className="admin-form">
                <h2>{isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>

                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="title">Title</label>
                    <input
                        id="title"
                        name="title"
                        type="text"
                        value={post.title}
                        onChange={handleChange}
                        required
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="slug">Slug</label>
                    <input
                        id="slug"
                        name="slug"
                        type="text"
                        value={post.slug}
                        onChange={handleChange}
                        required
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="author">Author</label>
                    <input
                        id="author"
                        name="author"
                        type="text"
                        value={post.author}
                        onChange={handleChange}
                        required
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    
                    <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor="content">Content</label>
                        <ReactQuill
                            theme="snow"
                            value={post.content}
                            onChange={(value) =>
                                setPost(prev => ({
                                    ...prev,
                                    content: value
                                }))
                            }
                            modules={BlogUpdateForm.modules}
                            formats={BlogUpdateForm.formats}
                            style={{ backgroundColor: '#fff', color: '#000' }} // Light background for readability
                        />
                    </div>

                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="imageUrl">Image URL</label>
                    <input
                        id="imageUrl"
                        name="imageUrl"
                        type="text"
                        value={post.imageUrl}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label>
                        <input
                            name="visible"
                            type="checkbox"
                            checked={post.visible}
                            onChange={handleChange}
                        />{' '}
                        Visible
                    </label>
                </div>

                {isEditMode && (
                    <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor="createdAt">Created At</label>
                        <input
                            id="createdAt"
                            name="createdAt"
                            type="date"
                            value={post.createdAt}
                            readOnly
                            style={{ width: '100%', padding: '0.5rem', backgroundColor: '#eee' }}
                        />
                    </div>
                )}

                <button type="submit" style={{ padding: '0.5rem 1rem' }}>
                    {isEditMode ? 'Update Post' : 'Create Post'}
                </button>
            </form>

            <hr style={{ margin: '2rem 0' }} />

            <div>
                <h3>Existing Posts</h3>
                <ul>
                    {allPosts.map(post => (
                        <li key={post.id}>
                            <Link to={`/admin/${post.id}`}>#{post.id} - {post.title}</Link>
                        </li>
                    ))}
                </ul>
            </div>
            </div>
        </div>
    );
};

export default BlogUpdateForm;
